import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";

const DEFAULT_WEB_APP_URL = "https://app.bilenia.se";

export function getBileniaWebAppBase(): string {
  const base =
    (import.meta.env.VITE_WEB_APP_URL as string | undefined)?.trim() ||
    DEFAULT_WEB_APP_URL;
  return base.replace(/\/+$/, "");
}

export function getBileniaWebAppUrl(path = "/dashboard"): string {
  const normalized = getBileniaWebAppBase();
  const route = path.startsWith("/") ? path : `/${path}`;
  return `${normalized}${route}`;
}

/**
 * Public URL safe to share outside the app.
 * Native Capacitor WebView runs on https://localhost — never share that origin.
 * Web (BrowserRouter) keeps the current href; native (HashRouter) maps #/path → https://app…/path.
 */
export function getShareableAppUrl(
  href: string = typeof window !== "undefined" ? window.location.href : "",
): string {
  if (!isNativeCapacitorApp()) {
    return href || (typeof window !== "undefined" ? window.location.href : "");
  }

  try {
    const parsed = new URL(href || window.location.href);
    const hash = parsed.hash.replace(/^#/, "");
    const pathWithQuery =
      hash && hash.length > 0
        ? hash.startsWith("/")
          ? hash
          : `/${hash}`
        : `${parsed.pathname || "/"}${parsed.search || ""}`;
    return `${getBileniaWebAppBase()}${pathWithQuery}`;
  } catch {
    return getBileniaWebAppBase();
  }
}

/** Open the web app in the system browser (native) or a new tab (web). */
export async function openBileniaWebApp(path = "/support"): Promise<void> {
  const url = getBileniaWebAppUrl(path);

  if (!isNativeCapacitorApp()) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }

  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
    return;
  } catch {
    /* @capacitor/browser not installed — fallback */
  }

  window.open(url, "_system");
}
